import {
  INSTALL_METHOD_LABELS,
  INSTALL_METHODS,
  ROUTE_CATEGORIES,
  ROUTE_CATEGORY_LABELS,
  ROUTE_MODE_LABELS,
  ROUTE_STATUS_LABELS,
  ROUTE_STATUSES,
  routeAttributesSchema,
  subject,
  type RouteDetail,
} from '@ftthnms/shared';
import { Button } from '@ftthnms/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Route as RouteIcon, Save } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ApiError, metres, send } from '../lib/api';
import { invalidateAssets } from '../lib/assets';
import { defaultsFor, useCableTypes, type RouteDraft } from '../lib/routes';
import { useSession } from '../lib/session';
import { Drawer } from './Drawer';

type Values = Record<string, string>;

/** Route attributes (RTE-05) for a drawn route or an existing one. */
export function RouteForm({
  mode,
  route,
  draft,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  route?: RouteDetail;
  draft?: RouteDraft;
  onClose: () => void;
  onSaved: (r: RouteDetail) => void;
}) {
  const { ability } = useSession();
  const qc = useQueryClient();
  const cableTypes = useCableTypes();
  const defaults = draft ? defaultsFor(draft.from.typeCode, draft.to.typeCode) : null;
  const [values, setValues] = useState<Values>(() => ({
    name: route?.name ?? '',
    category: route?.category ?? defaults?.category ?? 'distribution',
    cableType: route?.cableType ?? defaults?.cableType ?? '',
    coreCount: String(route?.coreCount ?? defaults?.coreCount ?? ''),
    status: route?.status ?? 'planned',
    installMethod: route?.installMethod ?? 'aerial',
    slackPct: String(route?.slackPct ?? 5),
    notes: route?.notes ?? '',
    color: route?.color ?? '',
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const areaCode = route?.areaCode ?? null;
  const canTech = mode === 'create' || ability.can('update', subject('Route', { areaCode }));
  const statusLocked = mode === 'create' && !ability.can('update', 'Route');
  const set = (k: string, v: string) => {
    setValues((s) => ({ ...s, [k]: v }));
    setErrors((e) => ({ ...e, [k]: '' }));
  };

  async function submit() {
    setFormError(null);
    const attrs = { ...values, status: statusLocked ? 'planned' : values.status };
    const parsed = routeAttributesSchema.safeParse(attrs);
    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const i of parsed.error.issues) found[String(i.path[0])] ??= i.message;
      setErrors(found);
      setFormError('Periksa kembali isian yang ditandai.');
      return;
    }
    setSaving(true);
    try {
      const saved =
        mode === 'create'
          ? await send<RouteDetail>('POST', '/api/v1/routes', {
              ...attrs,
              fromAssetId: draft!.from.id,
              toAssetId: draft!.to.id,
              mode: draft!.mode,
              path: draft!.path,
            })
          : await send<RouteDetail>('PUT', `/api/v1/routes/${route!.id}`, attrs);
      await invalidateAssets(qc);
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fieldErrors);
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const from = draft?.from.code ?? route?.from.code;
  const to = draft?.to.code ?? route?.to.code;

  return (
    <Drawer
      label={mode === 'create' ? 'Simpan jalur baru' : 'Edit jalur'}
      eyebrow={mode === 'create' ? 'Jalur kabel baru' : 'Edit jalur kabel'}
      title={`${from} → ${to}`}
      icon={
        <span className="fn-tile fn-tile--primary">
          <RouteIcon className="i" aria-hidden />
        </span>
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Batal
          </Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={() => void submit()}
            icon={mode === 'create' ? <Plus className="i" aria-hidden /> : <Save className="i" aria-hidden />}
          >
            {mode === 'create' ? 'Simpan Jalur' : 'Simpan'}
          </Button>
        </>
      }
    >
      {draft && (
        <div className="fn-choice">
          <span>
            <span className="fn-choice__title">
              {ROUTE_MODE_LABELS[draft.mode]} · {metres(draft.lengthM)}
            </span>
            <span className="fn-choice__desc">
              {draft.path.length} titik. Bentuk jalur bisa diubah setelah disimpan (Ubah bentuk).
            </span>
          </span>
        </div>
      )}
      {draft?.warning && (
        <div className="fn-alert fn-alert--warning" role="status">
          <div className="fn-alert__body">{draft.warning}</div>
        </div>
      )}
      {formError && (
        <div className="fn-alert fn-alert--danger" role="alert">
          <div className="fn-alert__body">{formError}</div>
        </div>
      )}
      {!canTech && (
        <div className="fn-alert fn-alert--info">
          <div className="fn-alert__body">
            <span className="fn-alert__text" style={{ color: 'inherit' }}>
              Sebagai teknisi Anda bisa mengubah status dan catatan jalur. Data teknis dikunci.
            </span>
          </div>
        </div>
      )}

      <div className="fn-form-grid">
        <Field id="r-name" label="Nama jalur" error={errors.name} wide hint="Opsional; default asal → tujuan">
          <input
            id="r-name"
            className="fn-input"
            value={values.name}
            disabled={!canTech}
            onChange={(e) => set('name', e.target.value)}
            placeholder={`${from} → ${to}`}
          />
        </Field>
        <Field
          id="r-status"
          label="Status"
          error={errors.status}
          hint={statusLocked ? 'Jalur baru dari teknisi berstatus Rencana' : undefined}
        >
          <select
            id="r-status"
            className="fn-input"
            value={statusLocked ? 'planned' : values.status}
            disabled={statusLocked}
            onChange={(e) => set('status', e.target.value)}
          >
            {ROUTE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ROUTE_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field id="r-category" label="Kategori" error={errors.category}>
          <select
            id="r-category"
            className="fn-input"
            value={values.category}
            disabled={!canTech}
            onChange={(e) => set('category', e.target.value)}
          >
            {ROUTE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {ROUTE_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field id="r-cable" label="Tipe kabel" error={errors.cableType}>
          <select
            id="r-cable"
            className="fn-input"
            value={values.cableType}
            disabled={!canTech}
            onChange={(e) => set('cableType', e.target.value)}
          >
            <option value="">—</option>
            {cableTypes.data?.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="r-cores" label="Jumlah core" error={errors.coreCount}>
          <input
            id="r-cores"
            className="fn-input fn-input--mono"
            inputMode="numeric"
            value={values.coreCount}
            disabled={!canTech}
            onChange={(e) => set('coreCount', e.target.value)}
          />
        </Field>
        <Field id="r-method" label="Metode instalasi" error={errors.installMethod}>
          <select
            id="r-method"
            className="fn-input"
            value={values.installMethod}
            disabled={!canTech}
            onChange={(e) => set('installMethod', e.target.value)}
          >
            {INSTALL_METHODS.map((m) => (
              <option key={m} value={m}>
                {INSTALL_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          id="r-slack"
          label="Cadangan kabel (slack)"
          error={errors.slackPct}
          hint="Ditambahkan ke panjang kabel"
        >
          <div className="fn-input-group">
            <input
              id="r-slack"
              className="fn-input fn-input--mono"
              inputMode="decimal"
              value={values.slackPct}
              disabled={!canTech}
              onChange={(e) => set('slackPct', e.target.value)}
            />
            <span className="fn-input-group__suffix">%</span>
          </div>
        </Field>
        <Field
          id="r-color"
          label="Warna garis"
          error={errors.color}
          hint={values.color ? 'Warna sendiri di peta' : 'Default: warna sesuai status'}
        >
          <div className="fn-color-field">
            <input
              id="r-color"
              type="color"
              className="fn-input fn-input--color"
              value={values.color || '#3b82f6'}
              disabled={!canTech}
              onChange={(e) => set('color', e.target.value)}
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={!canTech || !values.color}
              onClick={() => set('color', '')}
            >
              Default
            </Button>
          </div>
        </Field>
        <Field id="r-notes" label="Catatan" error={errors.notes} wide>
          <textarea
            id="r-notes"
            className="fn-input"
            rows={3}
            value={values.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}

function Field({
  id,
  label,
  error,
  hint,
  wide,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`fn-field${wide ? ' span-2' : ''}`}>
      <label className="fn-label" htmlFor={id}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="fn-hint fn-hint--error" role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="fn-hint">{hint}</span>
      )}
    </div>
  );
}
